import { CircularProgress } from "@material-ui/core";
import _ from "lodash";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { FlashMessage, ConfirmDialog } from "../../../components/common";
import CommentPost from "./CommentPost";
import Image from "../../../components/common/Image";
import { useDispatch, useSelector } from "react-redux";
import { rootReducersState } from "../../../reducers";
import {
  changeReplyData,
  commentLikeRequest,
  deleteCommentFlagReset,
  deleteCommentRequest,
} from "../../../reducers/feeds/feedPost.reducer";
import { ReducerFlag } from "../../../types/reducer.types";
import FeedCommentItem from "./FeedCommentItem";
import Report from "../../../components/common/Report";

interface ListProps {
  data: any;
  parentAuthorName?: string;
  postData?: any;
}

function List({ data, parentAuthorName = "", postData }: ListProps) {
  const listRef = useRef<HTMLUListElement>(null);
  const [editComment, setEditComment] = useState<any>({});
  const [commentId, setCommentId] = useState({});
  const sessionReducer = useSelector(
    ({ session }: rootReducersState) => session
  );
  const dispatch = useDispatch();

  const currentUserId = _.get(sessionReducer, "currentUser.id", "");
  const [openDeleteCommentModal, setOpenDeleteCommentModal] =
    useState<boolean>(false);

  const [openReplyReport, setOpenReplyReport] = useState(false);
  const [reportReplyId, setReportReplyId] = useState<number | null>(null);
  const {
    deleteCommentMessage,
    deleteCommentLoading,
    deleteCommentFlag,
    replyDeleteFlag,
    replyData,
    replyList,
    commentLikeFlag,
    commentLikedData,
    likeErrorMessage,
  } = useSelector(({ postFeed }: rootReducersState) => postFeed.postFeed);
  const { newReplyLoading, newReplyId } = useSelector(
    ({ postFeed }: rootReducersState) => postFeed.postFeed
  );
  const handleEdit = (reply: any) => {
    setEditComment(reply);
  };

  const setComment = () => {
    setEditComment({});
  };

  const handleLike = (value: any) => {
    dispatch(
      commentLikeRequest({ post_id: value.post_id, comment_id: value.id })
    );
  };

  const updateThreadTrunkHeight = useCallback(() => {
    const list = listRef.current;
    if (!list) return;

    const lastReply = list.lastElementChild as HTMLElement | null;
    if (!lastReply) {
      list.style.removeProperty("--last-reply-height");
      return;
    }

    list.style.setProperty("--last-reply-height", `${lastReply.offsetHeight}px`);
  }, []);

  useLayoutEffect(() => {
    updateThreadTrunkHeight();
  }, [data?.rows, editComment, newReplyLoading, newReplyId, updateThreadTrunkHeight]);

  useEffect(() => {
    const list = listRef.current;
    if (!list || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => {
      updateThreadTrunkHeight();
    });

    observer.observe(list);
    Array.from(list.children).forEach((child) => observer.observe(child));

    return () => observer.disconnect();
  }, [data?.rows, editComment, updateThreadTrunkHeight]);

  useEffect(() => {
    if ([ReducerFlag.SUCCESS, ReducerFlag.FAILED].includes(deleteCommentFlag)) {
      setOpenDeleteCommentModal(false);
      if (deleteCommentFlag === ReducerFlag.FAILED) {
        FlashMessage(deleteCommentMessage, "error");
      }
      dispatch(deleteCommentFlagReset());
    }

    // eslint-disable-next-line
  }, [deleteCommentFlag]);

  useEffect(() => {
    if (replyDeleteFlag === ReducerFlag.SUCCESS) {
      const oldData = replyList;
      const newData = oldData.map((item) => {
        if (parseInt(item.comment_id) === parseInt(replyData.parentId)) {
          let rows = item.rows.filter((reply: any) => {
            return reply.id !== replyData.id;
          });
          return {
            ...item,
            rows: rows || [],
          };
        } else {
          return item;
        }
      });
      setOpenDeleteCommentModal(false);
      dispatch(changeReplyData(newData));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replyDeleteFlag]);

  useEffect(() => {
    if (commentLikeFlag === ReducerFlag.SUCCESS) {
      if (commentLikedData.parentId != null) {
        const oldData = replyList;
        const parentId = commentLikedData.parentId;
        const likedCommentId = commentLikedData.commentId;
        const updatedData = oldData.map((item) => {
          if (String(item.comment_id) === String(parentId)) {
            const rows = (item.rows || []).map((row) => {
              if (Number(row.id) === Number(likedCommentId)) {
                return {
                  ...row,
                  liked: commentLikedData.liked,
                  comment_likes_count: commentLikedData.totalLikes,
                  is_edited: row.is_edited,
                };
              }
              return row;
            });
            return { ...item, rows };
          }
          return item;
        });
        dispatch(changeReplyData(updatedData));
      }
    } else if (commentLikeFlag === ReducerFlag.FAILED) {
      FlashMessage(likeErrorMessage, "error");
    }

    // eslint-disable-next-line
  }, [commentLikeFlag]);

  const renderReplyBody = (item: any) => {
    if (newReplyLoading && item.id === newReplyId) {
      return (
        <div className="loading-placeholder">
          <CircularProgress color="secondary" className="circular-progress" />
        </div>
      );
    }

    if (item.comment_type === "image") {
      return <Image src={item.comment_text} height="50px" />;
    }

    const commentHtml = item?.comment_text?.replace(/(\r\n|\n|\r)/gm, "<br/>") || "";

    return (
      <p className="comment-text">
      
        <span
          dangerouslySetInnerHTML={{
            __html: commentHtml,
          }}
        />
      </p>
    );
  };

  return (
    <ul ref={listRef} className="comment-replies-list">
      {data &&
        data?.rows &&
        data.rows.map((item: any, index) => {
          const isEditing = _.get(editComment, "id") === item.id;

          return (
            <li key={item.id ?? index}>
              <FeedCommentItem
                item={item}
                postOwnerId={item.post_user_id}
                currentUserId={currentUserId}
                isReply
                showReplyAction={false}
                onLike={handleLike}
                onEdit={handleEdit}
                isEditing={isEditing}
                editComposer={
                  isEditing ? (
                    <CommentPost
                      data={postData || data.rows}
                      item={item}
                      setComment={setComment}
                      reason="edit-comment"
                    />
                  ) : undefined
                }
                onDelete={() => {
                  setCommentId({
                    postId: item.post_id,
                    parentId: item.parent_id,
                    id: item.id,
                    isLast: data?.rows.length === 1,
                  });
                  setOpenDeleteCommentModal(true);
                }}
                onReport={() => {
                  setReportReplyId(item.id);
                  setOpenReplyReport(true);
                }}
                body={renderReplyBody(item)}
              />
            </li>
          );
        })}
      {openReplyReport && reportReplyId && (
        <Report
          titleText="Report this reply"
          referenceId={reportReplyId}
          type="post_reply"
          onClose={() => {
            setOpenReplyReport(false);
            setReportReplyId(null);
          }}
          openReports={true}
        />
      )}
      <ConfirmDialog
        visible={openDeleteCommentModal}
        title="Delete reply?"
        bodyText="Are you sure you want to delete this reply? This action cannot be undone."
        confirmText="Delete"
        loading={deleteCommentLoading}
        onConfirm={() => dispatch(deleteCommentRequest(commentId))}
        onCancel={() => setOpenDeleteCommentModal(false)}
      />
    </ul>
  );
}

export default List;
